'use strict';

export class Dialog {
	static initialized = false;
	constructor(component_key) {
		this.id = component_key;
		this.component = yalwa.v.components[component_key];
		this.$dialog = jQuery(`.js-${this.id}`);
		if (this.component.trigger) {
			this.$trigger = jQuery(`.${this.component.trigger}`);
		}
	}
	init() {
		if (!Dialog.initialized) {
			Dialog.initialized = true;
			this.init_once();
			return;
		}
		// A lazy dialog registered after page load arrives with no events bound: init_once()
		// has already run and will not run again. Only lazy dialogs take this path, so the
		// dialogs shipped with the page keep binding exactly once, in init_once().
		if (this.component.lazy && !this.events_bound) {
			this.load();
		}
	}
	async init_once() {
		const promises = Object.keys(yalwa.v.components).map(async component_key => {
			const component = yalwa.v.components[component_key];
			if (component_key.startsWith('dialog_')) {
				try {
					const class_name = yalwa.basics.to_pascal_case(component_key);
					// import
					const module = await import(`./${component_key}.js`);
					// instantiate and initialize
					const component_class = module[class_name];
					const instance = new component_class(component_key);
					//Making it available in public
					(yalwa.v ??= {}).components ??= {};
					yalwa.v.components.initialized ??= {};
					yalwa.v.components.initialized[component_key] = instance;
					// Other yComponents reach dialogs through component_instances (e.g.
					// chatwallpaper closes its own dialog), so register there as well and
					// keep the two maps pointing at the same instance.
					(yalwa.v.component_instances ??= {})[component_key] = instance;
					if (component.default_visible) {
						yalwa.v.dialog_queue.push(instance); // other dialogs with default visible value
					} else {
						instance.load();
					}
				} catch (error) {
					console.error(`Failed to load or initialize class for ${component_key}:`, error);
				}
			}
		})
		// Wait for all promises to resolve
		await Promise.all(promises);
		this.prepare_queue();
		this.check_queue();
	}
	load() {
		this.add_events();
		this.events_bound = true;
		return this;
	}
	
	check_queue() {
		if (yalwa.v.dialog_queue[0]) {
			const dialog = yalwa.v.dialog_queue.shift();
			dialog.load().show();
		}
	}
	add_events() {
		// Generic close event
		if(this.$dialog.hasClass(`js-${this.component.component_type}_close`)) {
			this.$dialog.off('click.close').on('click.close', (e) => {
				// Only close if the click was directly on the background overlay, not bubbled from content
				if (e.target === e.currentTarget || jQuery(e.target).hasClass('dialog__position')) {
					this.hide();
				}
			});
		}
		if (this.$trigger) {
			// show() fetches a lazy dialog on its own, so the trigger binds the same way for
			// both kinds.
			this.$trigger.off('click.dialog_lazy').on('click.dialog_lazy', this.show.bind(this));
		}
		this.$dialog.find(`.js-${this.component.component_type}_close`).off('click.close').on('click.close', this.hide.bind(this));
	}

	/**
	 * Fetches this dialog's markup once, then calls back. A lazy dialog is registered with
	 * its configuration only, so there is nothing to show until the endpoint answers.
	 *
	 * @param {Function} on_loaded Called after the markup is in place and events are bound.
	 */
	fetch(on_loaded) {
		if (this.fetch_promise) {
			return this.fetch_promise;
		}
		this.fetch_promise = jQuery.ajax({
			url: '/api/ajax/general',
			type: 'get',
			dataType: 'json',
			data: {action: 'dialog', name: this.component.type},
		}).then((response) => {
			if (!response || !response.success || !response.html) {
				return;
			}
			return yalwa.mobile.assets_loader().load_assets_and_append_html({
				...response,
				html: jQuery(response.html),
				$append_to: jQuery('body'),
			}).then(() => {
				// The markup replaced the placeholder this instance was constructed around.
				this.$dialog = jQuery(`.js-${this.id}`);
				// assets_loader merges the served config by replacing yalwa.v.components with a
				// new object, so the reference taken in the constructor still points at the
				// page-load shell config. Re-read it to pick up the keys only the endpoint
				// knows (is_premium drives the promo CTA).
				this.component = (yalwa.v.components || {})[this.id] || this.component;
				this.lazy_loaded = true;
				this.load();
				// Dialogs live in two registries: dialog.js owns components.initialized,
				// assets_loader writes component_instances, and callers such as
				// close_dialog() read the latter. Point both at the bound instance so a
				// later hide() cannot land on the stale placeholder.
				(yalwa.v.component_instances ??= {})[this.id] = this;
				(yalwa.v.components.initialized ??= {})[this.id] = this;
				if (on_loaded) {
					on_loaded();
				}
			});
		}).always(() => {
			this.fetch_promise = null;
		});
		return this.fetch_promise;
	}
	hide() {
		yalwa.basics.hide(this.$dialog);
		yalwa.basics.bem_modifier(jQuery('.js-mbody'), 'gs_showing', 'remove');
		this.on_close();
		this.check_queue();
	}
	on_close() {
		//do nothing by default
	}
	on_show() {
		//do nothing by default
	}
	show(...args) {
		// A lazy dialog has no markup until its endpoint answers, so the first show has to
		// fetch it. Every caller (trigger, auto-open queue, another component) goes through
		// here, which keeps the fetch in one place. The arguments are replayed after the
		// fetch so a subclass that reads them (the promo touchpoint) still sees them.
		if (this.component.lazy && !this.lazy_loaded) {
			this.fetch(() => this.show(...args));
			return this;
		}
		yalwa.basics.show(this.$dialog);
		yalwa.basics.bem_modifier(jQuery('.js-mbody'), 'gs_showing', 'add');
		this.on_show();
		return this;
	}
	prepare_queue() {
		[...yalwa.v.dialog_queue.entries()]
			.sort(([, a], [, b]) => {
				return a.component.priority - b.component.priority;
			})
			.forEach(([key, value], index) => {
				yalwa.v.dialog_queue[index] = value;
				if (index !== 0) {
					yalwa.basics.hide(yalwa.v.dialog_queue[index].$dialog);
				}
			});
	}
}

yalwa.v.dialog_queue = [];
